import React, { useEffect, useState, useCallback } from 'react';
import {
  CCard, CCardBody, CCardHeader, CRow, CCol, CSpinner, CAlert, CBadge,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const STATUS_BADGE = {
  'Baru':                 'primary',
  'Dalam Proses':         'warning',
  'Selesai':              'success',
  'Dipulangkan':          'info',
  'Menunggu Maklumbalas': 'secondary',
  'Waiting to Pickup':    'dark',
};

const ALL_STATUSES = ['Baru','Dalam Proses','Selesai','Dipulangkan','Menunggu Maklumbalas','Waiting to Pickup'];

export default function AdminPerformancePage() {
  const navigate = useNavigate();
  const now = new Date();
  const [years, setYears]   = useState([now.getFullYear()]);
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [week, setWeek]     = useState(null); // null = all, 1-4 = specific week
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    api.get('/admin/performance/years')
      .then(r => {
        const ys = r.data.years || [];
        setYears(ys.length ? ys : [now.getFullYear()]);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { year, month };
      if (week) params.week = week;
      const { data: d } = await api.get('/admin/performance', { params });
      setData(d);
    } catch {
      setError('Failed to load performance data.');
    } finally {
      setLoading(false);
    }
  }, [year, month, week]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">Technician Performance</h5></CCol>
        <CCol xs="auto" className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm"
            style={{ width: 90 }}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </CCol>
      </CRow>

      {/* Month + Week selector */}
      <CCard className="mb-3 border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <CCardBody className="py-2 d-flex flex-column gap-2">
          {/* Month row */}
          <div className="d-flex flex-wrap gap-2">
            {MONTHS.map((m, i) => {
              const mn = i + 1;
              const isActive = mn === month;
              const isFuture = year === now.getFullYear() && mn > now.getMonth() + 1;
              return (
                <button
                  key={mn}
                  onClick={() => { if (!isFuture) { setMonth(mn); setWeek(null); } }}
                  disabled={isFuture}
                  style={{
                    padding: '5px 14px',
                    borderRadius: 20,
                    border: isActive ? '2px solid #8bc34a' : '1.5px solid #dee2e6',
                    background: isActive ? '#8bc34a' : 'transparent',
                    color: isActive ? '#fff' : isFuture ? '#bbb' : '#333',
                    fontWeight: isActive ? 700 : 400,
                    fontSize: 13,
                    cursor: isFuture ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>
          {/* Week row */}
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <span className="text-muted small me-1">Week:</span>
            {[null, 1, 2, 3, 4].map(w => {
              const isActive = week === w;
              return (
                <button
                  key={String(w)}
                  onClick={() => setWeek(w)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    border: isActive ? '2px solid #4e73df' : '1.5px solid #dee2e6',
                    background: isActive ? '#4e73df' : 'transparent',
                    color: isActive ? '#fff' : '#333',
                    fontWeight: isActive ? 700 : 400,
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {w === null ? 'All' : `Week ${w}`}
                </button>
              );
            })}
          </div>
        </CCardBody>
      </CCard>

      {error && <CAlert color="danger">{error}</CAlert>}

      <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white fw-semibold">
          {MONTHS[month - 1]} {year}{week ? ` — Week ${week}` : ''} — Staff Performance
        </CCardHeader>
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="success" /></div>
          ) : data?.staff?.length === 0 ? (
            <div className="text-center text-muted py-5">No technician staff found.</div>
          ) : (
            <CTable hover responsive className="mb-0">
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>Staff</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Total Jobs</CTableHeaderCell>
                  <CTableHeaderCell className="text-center text-success">Completed</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Invoices</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Invoiced (RM)</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Paid (RM)</CTableHeaderCell>
                  {ALL_STATUSES.map(s => (
                    <CTableHeaderCell key={s} className="text-center">
                      <CBadge color={STATUS_BADGE[s]} style={{ fontSize: 11, fontWeight: 500 }}>{s}</CBadge>
                    </CTableHeaderCell>
                  ))}
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {data?.staff?.map(s => (
                  <CTableRow key={s.username}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const mm = String(month).padStart(2, '0');
                      const params = new URLSearchParams({
                        admin_id: s.username,
                        datePattern: `%/${mm}/${year}`,
                        label: `${s.nama} · ${MONTHS[month - 1]} ${year}`,
                      });
                      navigate(`/jobs?${params.toString()}`);
                    }}
                  >
                    <CTableDataCell>
                      <div className="fw-semibold d-flex align-items-center gap-2">
                        {s.nama}
                        {!s.isactive && <CBadge color="danger" style={{ fontSize: 10 }}>Inactive</CBadge>}
                      </div>
                      <div className="text-muted small">{s.username}</div>
                    </CTableDataCell>
                    <CTableDataCell className="text-center fw-bold">{s.total}</CTableDataCell>
                    <CTableDataCell className="text-center">
                      <span className="fw-bold text-success">{s.completed}</span>
                    </CTableDataCell>
                    <CTableDataCell className="text-center">{s.inv_count || <span className="text-muted">—</span>}</CTableDataCell>
                    <CTableDataCell className="text-center">{s.inv_total > 0 ? s.inv_total.toFixed(2) : <span className="text-muted">—</span>}</CTableDataCell>
                    <CTableDataCell className="text-center">
                      {s.inv_paid > 0
                        ? <span className={s.inv_paid >= s.inv_total ? 'text-success fw-semibold' : ''}>{s.inv_paid.toFixed(2)}</span>
                        : <span className="text-muted">—</span>}
                    </CTableDataCell>
                    {ALL_STATUSES.map(st => (
                      <CTableDataCell key={st} className="text-center">
                        {s.byStatus[st] > 0
                          ? <CBadge color={STATUS_BADGE[st]}>{s.byStatus[st]}</CBadge>
                          : <span className="text-muted">—</span>}
                      </CTableDataCell>
                    ))}
                  </CTableRow>
                ))}
                {/* Totals row */}
                {data?.staff?.length > 1 && (() => {
                  const totals = ALL_STATUSES.reduce((acc, st) => {
                    acc[st] = data.staff.reduce((s, r) => s + (r.byStatus[st] || 0), 0);
                    return acc;
                  }, {});
                  const grandTotal     = data.staff.reduce((s, r) => s + r.total, 0);
                  const grandCompleted = data.staff.reduce((s, r) => s + r.completed, 0);
                  const grandInvCount  = data.staff.reduce((s, r) => s + r.inv_count, 0);
                  const grandInvTotal  = Math.round(data.staff.reduce((s, r) => s + r.inv_total, 0) * 100) / 100;
                  const grandInvPaid   = Math.round(data.staff.reduce((s, r) => s + r.inv_paid,  0) * 100) / 100;
                  return (
                    <CTableRow style={{ background: '#f8f9fa', fontWeight: 600 }}>
                      <CTableDataCell className="text-muted small">TOTAL</CTableDataCell>
                      <CTableDataCell className="text-center fw-bold">{grandTotal}</CTableDataCell>
                      <CTableDataCell className="text-center fw-bold text-success">{grandCompleted}</CTableDataCell>
                      <CTableDataCell className="text-center">{grandInvCount || '—'}</CTableDataCell>
                      <CTableDataCell className="text-center">{grandInvTotal > 0 ? grandInvTotal.toFixed(2) : '—'}</CTableDataCell>
                      <CTableDataCell className="text-center">{grandInvPaid > 0 ? grandInvPaid.toFixed(2) : '—'}</CTableDataCell>
                      {ALL_STATUSES.map(st => (
                        <CTableDataCell key={st} className="text-center">
                          {totals[st] > 0 ? totals[st] : <span className="text-muted">—</span>}
                        </CTableDataCell>
                      ))}
                    </CTableRow>
                  );
                })()}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
